import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Maximize, Minimize } from "lucide-react";
import { API, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { sfx } from "@/lib/audio";
import { useTick } from "@/lib/hooks";
import { canFullscreen, isFullscreen, onFullscreenChange, toggleFullscreen } from "@/lib/fullscreen";
import { MuteToggle } from "@/components/Toggles";
import { AnswerGrid, Big, Center, CRTFrame, FinalView, IntroView, LockedView, RankLine, RevealView, SalesmanPortrait } from "@/components/player/PlayerViews";

function usePlayerState(session, nav) {
  const [st, setSt] = useState(null);
  const [removed, setRemoved] = useState(false);
  const offset = useRef(0);
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/play/${session.game_id}/state`, { params: { player_id: session.player_id } });
      offset.current = data.server_now - Date.now();
      setSt(data);
    } catch (e) {
      const code = e.response?.status;
      if (code === 410) setRemoved(true);
      if (code === 404) { localStorage.removeItem("sq_player"); nav("/"); }
    }
  }, [session, nav]);
  useEffect(() => {
    if (!session) { nav("/"); return undefined; }
    load();
    const id = setInterval(load, 900);
    return () => clearInterval(id);
  }, [session, load, nav]);
  return { st, removed, offset };
}

export default function PlayerGame() {
  const nav = useNavigate();
  const { t, setLang } = useLang();
  const session = useMemo(() => JSON.parse(localStorage.getItem("sq_player") || "null"), []);
  const { st, removed, offset } = usePlayerState(session, nav);
  const [mine, setMine] = useState(null);
  const [changing, setChanging] = useState(false);
  const submitting = useRef(false);
  const now = useTick(250) + offset.current;

  useEffect(() => { setChanging(false); }, [st?.idx, st?.phase]);

  useEffect(() => {
    if (st?.language) setLang(st.language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st?.language]);

  const revealKey = st?.phase === "reveal" ? st.idx : null;
  useEffect(() => {
    if (revealKey === null) return;
    if (st.correct) { sfx.correct(); navigator.vibrate?.([60, 40, 60]); } else { sfx.wrong(); navigator.vibrate?.(250); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealKey]);

  const answer = async (i) => {
    if (submitting.current) return;
    submitting.current = true;
    const endsAt = st.ends_at;
    setMine({ idx: st.idx, option: i });
    setChanging(false);
    sfx.tap();
    navigator.vibrate?.(40);
    let ok = false;
    for (let tryN = 0; tryN < 3 && !ok; tryN++) {
      try {
        await axios.post(`${API}/play/${session.game_id}/answer`, { player_id: session.player_id, option: i });
        ok = true;
      } catch (e) {
        if (e.response?.status === 400) break; // answering genuinely closed
        await new Promise((r) => setTimeout(r, 400)); // transient/network hiccup: retry, keep the lock
      }
    }
    submitting.current = false;
    // Only drop the lock if the window has truly ended; otherwise keep it so a
    // brief network blip never bounces the player back to the buttons.
    if (!ok && Date.now() + offset.current > endsAt) { setMine(null); toast.error(t("answering_closed")); }
  };

  const again = () => { localStorage.removeItem("sq_player"); nav("/"); };

  const submitContact = async ({ name, store }) => {
    try {
      await axios.post(`${API}/play/${session.game_id}/contact`, { player_id: session.player_id, name, store });
      sfx.correct();
    } catch (e) {
      toast.error(errMsg(e, t("answering_closed")));
      throw e;
    }
  };

  const [fs, setFs] = useState(isFullscreen());
  useEffect(() => onFullscreenChange(setFs), []);

  if (removed) return <div className="player-root"><Center testId="player-removed-view"><Big>{t("removed")}</Big><button className="px-btn text-xs" onClick={again}>{t("play_again")}</button></Center></div>;
  if (!st) return <div className="player-root"><Center><div className="font-press blink">…</div></Center></div>;

  let body;
  if (st.phase === "lobby") {
    body = (
      <Center testId="player-lobby-view">
        <CRTFrame testId="player-crt-portrait" className="float-y">
          <SalesmanPortrait shirt={st.shirt_color} />
        </CRTFrame>
        <Big>{t("youre_in")}</Big>
        <div className="font-pixel text-2xl text-[#FFD700]">{st.name}</div>
        <div className="font-pixel text-slate-300">{t("see_name")}</div>
      </Center>
    );
  } else if (st.phase === "intro") {
    body = <IntroView st={st} t={t} />;
  } else if (st.phase === "question") {
    const locked = (st.answered || mine?.idx === st.idx) && !changing;
    if (now < st.options_at) {
      body = (
        <Center testId="player-get-ready-view">
          <div className="font-silk text-slate-300">{t("question_n", { n: st.idx + 1 })}</div>
          <Big>{t("get_ready")}</Big>
          <div className="font-num text-7xl text-[#FFD700] leading-none">{Math.max(1, Math.ceil((st.options_at - now) / 1000))}</div>
          <div className="font-pixel text-slate-400">{t("look_screen")}</div>
        </Center>
      );
    } else if (locked) {
      body = <LockedView option={mine?.idx === st.idx ? mine.option : st.my_option} t={t} canChange={now < st.ends_at} onChange={() => setChanging(true)} />;
    } else if (now > st.ends_at + 800) {
      body = <Center testId="player-timesup-view"><Big>{t("times_up")}</Big></Center>;
    } else {
      body = <AnswerGrid st={st} onAnswer={answer} />;
    }
  } else if (st.phase === "reveal") {
    body = <RevealView st={st} t={t} />;
  } else if (st.phase === "scoreboard") {
    body = <Center testId="player-scoreboard-view"><Big>{t("score")}: <span className="font-num">{st.score}</span></Big><RankLine st={st} t={t} /></Center>;
  } else {
    body = <FinalView st={st} t={t} onAgain={again} onSubmitContact={submitContact} />;
  }

  return (
    <div className="player-root" data-testid="player-game-screen">
      <div className="flex items-center justify-between px-4 py-3 bg-black/50">
        <span className="font-pixel font-bold text-lg truncate max-w-[45%]" data-testid="player-name-display">{st.name}</span>
        <div className="flex items-center gap-3">
          <span className="font-num text-xl bg-white text-black px-3 py-1.5 rounded leading-none" data-testid="player-score-display">{st.score}</span>
          {canFullscreen() && (
            <button data-testid="player-fullscreen-toggle-button" aria-label="Toggle fullscreen" onClick={toggleFullscreen} className="text-white p-1">
              {fs ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </button>
          )}
          <MuteToggle className="text-white p-1" />
        </div>
      </div>
      {body}
    </div>
  );
}
